<template>
  <div class="automations-container">
    <div class="page-header">
      <h1 class="text-2xl font-semibold text-gray-900">Automations</h1>
      <p class="text-gray-600 mt-2">Build and manage workflows to automate your supply chain processes</p>
    </div>

    <div class="content-section">
      <div class="flex justify-between items-center mb-6">
        <div class="flex space-x-2">
          <button @click="createNewWorkflow" class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
            </svg>
            New Workflow
          </button>
          <button class="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            Import
          </button>
        </div>
        <div class="relative">
          <input type="text" placeholder="Search workflows..." class="border border-gray-300 rounded-md px-4 py-2 pl-10 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>

      <!-- Workflow List -->
      <div v-if="!showWorkflowEditor" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div v-for="workflow in workflows" :key="workflow.id" class="workflow-card bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
          <div class="p-5">
            <div class="flex justify-between items-start">
              <div>
                <h3 class="text-lg font-medium text-gray-900">{{ workflow.name }}</h3>
                <p class="text-sm text-gray-500 mt-1">{{ workflow.description }}</p>
              </div>
              <div class="flex space-x-2">
                <button @click="editWorkflow(workflow)" class="text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
                <button class="text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="mt-4 flex items-center justify-between">
              <div class="flex items-center">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                      :class="workflow.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'">
                  {{ workflow.active ? 'Active' : 'Inactive' }}
                </span>
                <span class="ml-2 text-xs text-gray-500">Last run: {{ workflow.lastRun }}</span>
              </div>
              <button @click="toggleWorkflowStatus(workflow)" 
                      :class="workflow.active ? 'bg-green-500' : 'bg-gray-300'"
                      class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
                <span :class="workflow.active ? 'translate-x-5' : 'translate-x-0'" class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
              </button>
            </div>
          </div>
          <div class="border-t border-gray-200 bg-gray-50 px-5 py-3 text-sm">
            <div class="flex justify-between items-center">
              <div class="text-gray-500">{{ workflow.nodeCount }} nodes</div>
              <button @click="editWorkflow(workflow)" class="text-green-600 hover:text-green-700 font-medium">Edit Workflow</button>
            </div>
          </div>
        </div>

        <!-- Create New Workflow Card -->
        <div @click="createNewWorkflow" class="workflow-card bg-white rounded-lg border border-dashed border-gray-300 overflow-hidden hover:border-green-500 hover:shadow-md transition-all cursor-pointer flex items-center justify-center p-10">
          <div class="text-center">
            <div class="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <h3 class="mt-2 text-sm font-medium text-gray-900">Create new workflow</h3>
            <p class="mt-1 text-sm text-gray-500">Start building your automation</p>
          </div>
        </div>
      </div>

      <!-- Workflow Editor -->
      <div v-if="showWorkflowEditor" class="workflow-editor">
        <div class="editor-header bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center">
          <div class="flex items-center">
            <button @click="closeEditor" class="mr-4 text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
              </svg>
            </button>
            <input v-model="currentWorkflow.name" type="text" class="text-lg font-medium border-none focus:outline-none focus:ring-0" placeholder="Untitled Workflow">
          </div>
          <div class="flex space-x-3">
            <button class="border border-gray-300 hover:bg-gray-50 px-3 py-1 rounded-md text-sm">
              Test
            </button>
            <button class="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm">
              Save
            </button>
          </div>
        </div>

        <div class="editor-content flex h-[calc(100vh-13rem)]">
          <!-- Node Palette -->
          <div class="node-palette w-64 bg-white border-r border-gray-200 overflow-y-auto">
            <div class="p-4">
              <h3 class="text-sm font-medium text-gray-900 mb-3">Nodes</h3>
              <div class="relative">
                <input type="text" placeholder="Search nodes..." class="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>

            <div class="node-categories">
              <div v-for="(category, index) in nodeCategories" :key="index" class="node-category">
                <div @click="toggleCategory(index)" class="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50">
                  <span class="text-sm font-medium text-gray-700">{{ category.name }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" :class="{'transform rotate-180': category.expanded}" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div v-if="category.expanded" class="node-list">
                  <div v-for="node in category.nodes" :key="node.type" 
                       @dragstart="dragStart($event, node)" 
                       draggable="true"
                       class="node-item flex items-center px-4 py-2 cursor-grab hover:bg-gray-50">
                    <div class="node-icon w-6 h-6 rounded flex items-center justify-center mr-3" :style="{ backgroundColor: node.color }">
                      <span class="text-white text-xs font-bold">{{ node.icon }}</span>
                    </div>
                    <span class="text-sm text-gray-700">{{ node.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Canvas -->
          <div class="workflow-canvas flex-1 bg-gray-100 overflow-auto relative" @drop="onDrop" @dragover.prevent>
            <div v-if="currentWorkflow.nodes.length === 0" class="absolute inset-0 flex items-center justify-center text-gray-400">
              <div class="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                <p class="mt-2">Drag and drop nodes to build your workflow</p>
              </div>
            </div>

            <!-- Workflow Nodes -->
            <div v-for="node in currentWorkflow.nodes" :key="node.id" 
                 class="workflow-node absolute bg-white rounded-md shadow-md border border-gray-200 w-64"
                 :style="{ left: node.position.x + 'px', top: node.position.y + 'px' }"
                 @mousedown="startDrag($event, node)">
              <div class="node-header px-3 py-2 flex items-center justify-between rounded-t-md" :style="{ backgroundColor: node.color }">
                <div class="flex items-center">
                  <span class="text-white font-medium text-sm">{{ node.name }}</span>
                </div>
                <div class="flex space-x-1">
                  <button @click="deleteNode(node)" class="text-white opacity-70 hover:opacity-100">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="node-body p-3">
                <div v-for="(field, fieldIndex) in node.fields" :key="fieldIndex" class="mb-2">
                  <label class="block text-xs font-medium text-gray-700 mb-1">{{ field.label }}</label>
                  <input v-if="field.type === 'text'" v-model="field.value" type="text" class="w-full border border-gray-300 rounded-sm px-2 py-1 text-sm">
                  <select v-else-if="field.type === 'select'" v-model="field.value" class="w-full border border-gray-300 rounded-sm px-2 py-1 text-sm">
                    <option v-for="option in field.options" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                </div>
              </div>
              <div class="node-footer px-3 py-2 bg-gray-50 rounded-b-md border-t border-gray-200 flex justify-between items-center">
                <span class="text-xs text-gray-500">{{ node.type }}</span>
                <div class="flex space-x-1">
                  <div class="node-input w-3 h-3 rounded-full bg-gray-300"></div>
                  <div class="node-output w-3 h-3 rounded-full bg-gray-300"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AutomationsPage',
  data() {
    return {
      showWorkflowEditor: false,
      workflows: [
        {
          id: 1,
          name: 'Daily Farm Data Sync',
          description: 'Automatically sync farm data from multiple sources daily',
          active: true,
          lastRun: '2 hours ago',
          nodeCount: 5
        },
        {
          id: 2,
          name: 'Wool Quality Alert',
          description: 'Send alerts when wool quality metrics fall below thresholds',
          active: false,
          lastRun: 'Yesterday',
          nodeCount: 3
        }
      ],
      currentWorkflow: {
        id: null,
        name: '',
        description: '',
        active: false,
        nodes: []
      },
      nodeCategories: [
        {
          name: 'Triggers',
          expanded: true,
          nodes: [
            { type: 'webhook', name: 'Webhook', icon: 'W', color: '#3B82F6' },
            { type: 'schedule', name: 'Schedule', icon: 'S', color: '#8B5CF6' },
            { type: 'form', name: 'Form Trigger', icon: 'F', color: '#EC4899' }
          ]
        },
        {
          name: 'Operations',
          expanded: false,
          nodes: [
            { type: 'filter', name: 'Filter', icon: 'Fi', color: '#10B981' },
            { type: 'transform', name: 'Transform', icon: 'T', color: '#F59E0B' },
            { type: 'merge', name: 'Merge', icon: 'M', color: '#6366F1' }
          ]
        },
        {
          name: 'Data Sources',
          expanded: false,
          nodes: [
            { type: 'database', name: 'Database', icon: 'DB', color: '#EF4444' },
            { type: 'api', name: 'API Request', icon: 'API', color: '#3B82F6' },
            { type: 'file', name: 'File', icon: 'F', color: '#8B5CF6' }
          ]
        },
        {
          name: 'Actions',
          expanded: false,
          nodes: [
            { type: 'email', name: 'Send Email', icon: 'E', color: '#F59E0B' },
            { type: 'notification', name: 'Notification', icon: 'N', color: '#10B981' },
            { type: 'http', name: 'HTTP Request', icon: 'H', color: '#6366F1' }
          ]
        }
      ],
      draggedNode: null,
      nodeCounter: 1,
      isDragging: false,
      draggedElement: null,
      initialX: 0,
      initialY: 0,
      offsetX: 0,
      offsetY: 0
    }
  },
  methods: {
    createNewWorkflow() {
      this.currentWorkflow = {
        id: null,
        name: 'Untitled Workflow',
        description: '',
        active: false,
        nodes: []
      };
      this.showWorkflowEditor = true;
    },
    editWorkflow(workflow) {
      // In a real app, we would fetch the workflow details from the server
      this.currentWorkflow = JSON.parse(JSON.stringify(workflow));
      this.currentWorkflow.nodes = this.currentWorkflow.nodes || [];
      this.showWorkflowEditor = true;
    },
    closeEditor() {
      this.showWorkflowEditor = false;
    },
    toggleWorkflowStatus(workflow) {
      workflow.active = !workflow.active;
    },
    toggleCategory(index) {
      this.nodeCategories[index].expanded = !this.nodeCategories[index].expanded;
    },
    dragStart(event, node) {
      this.draggedNode = { ...node };
      event.dataTransfer.effectAllowed = 'move';
    },
    onDrop(event) {
      event.preventDefault();
      if (this.draggedNode) {
        const rect = event.target.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // Create a new node instance
        const newNode = {
          id: `node_${this.nodeCounter++}`,
          type: this.draggedNode.type,
          name: this.draggedNode.name,
          color: this.draggedNode.color,
          position: { x, y },
          fields: this.getNodeFields(this.draggedNode.type)
        };
        
        this.currentWorkflow.nodes.push(newNode);
        this.draggedNode = null;
      }
    },
    getNodeFields(type) {
      // Return different fields based on node type
      switch(type) {
        case 'webhook':
          return [
            { label: 'Webhook URL', type: 'text', value: '' }
          ];
        case 'schedule':
          return [
            { label: 'Frequency', type: 'select', value: 'daily', options: [
              { label: 'Hourly', value: 'hourly' },
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
              { label: 'Monthly', value: 'monthly' }
            ]}
          ];
        case 'email':
          return [
            { label: 'To', type: 'text', value: '' },
            { label: 'Subject', type: 'text', value: '' }
          ];
        default:
          return [
            { label: 'Name', type: 'text', value: '' }
          ];
      }
    },
    startDrag(event, node) {
      if (event.target.closest('.node-header')) {
        this.isDragging = true;
        this.draggedElement = node;
        
        const rect = event.currentTarget.getBoundingClientRect();
        this.offsetX = event.clientX - rect.left;
        this.offsetY = event.clientY - rect.top;
        
        document.addEventListener('mousemove', this.onDrag);
        document.addEventListener('mouseup', this.stopDrag);
      }
    },
    onDrag(event) {
      if (this.isDragging && this.draggedElement) {
        const canvas = document.querySelector('.workflow-canvas');
        const rect = canvas.getBoundingClientRect();
        
        const x = event.clientX - rect.left - this.offsetX;
        const y = event.clientY - rect.top - this.offsetY;
        
        this.draggedElement.position.x = Math.max(0, x);
        this.draggedElement.position.y = Math.max(0, y);
      }
    },
    stopDrag() {
      this.isDragging = false;
      this.draggedElement = null;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    deleteNode(node) {
      const index = this.currentWorkflow.nodes.findIndex(n => n.id === node.id);
      if (index !== -1) {
        this.currentWorkflow.nodes.splice(index, 1);
      }
    }
  }
}
</script>

<style scoped>
.automations-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.content-section {
  margin-bottom: 32px;
}

.workflow-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.workflow-editor {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.node-item {
  transition: background-color 0.2s;
}

.workflow-node {
  user-select: none;
}

.node-input, .node-output {
  cursor: pointer;
}
</style>
